<template>
  <div>
    <page-title-bar :customTitle="customTitle"></page-title-bar>
    <v-container fluid grid-list-xl pt-0>
      <v-layout row wrap justify-center>
        <v-flex xs12>
          <v-toolbar :elevation="0" height="auto">
            <v-layout row justify-start align-center style="padding: 32px">
              <v-flex xs12 md4>
                <div style="max-width: 290px">
                  <v-menu :close-on-content-click="false" v-model="menu_month_picker" :nudge-right="40" lazy
                    transition="scale-transition" offset-y full-width min-width="290px" max-width="290px"
                    :disabled="loader">
                    <v-text-field slot="activator" v-model="chosen_month" label="Chọn tháng so sánh" :disabled="loader"
                      prepend-icon="event" readonly hide-details></v-text-field>
                    <v-date-picker min="2025-04" locale="vi-VI" type="month" v-model="chosen_month"
                      @input="menu_month_picker = false"></v-date-picker>
                  </v-menu>
                </div>
              </v-flex>

              <v-flex xs12 md8 class="text-right">
                 <v-btn color="success" @click="exportExcel" :loading="exporting" :disabled="loader || !comparison">
                    <v-icon left small>cloud_download</v-icon>
                    Xuất Excel
                 </v-btn>
              </v-flex>
            </v-layout>
          </v-toolbar>

          <div class="mt-3">
            <v-card>
              <v-card-title class="headline">
                So sánh báo cáo tình hình tài chính: {{ formatMonthDisplay(previousMonth) }} vs {{ formatMonthDisplay(chosen_month) }}
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text>
                <!-- Error/Warning messages -->
                <v-alert v-if="errorMessage" :value="true" type="warning" class="mb-4">
                  {{ errorMessage }}
                </v-alert>

                <div v-if="comparison && !errorMessage" class="table-responsive">
                  <table class="comparison-table">
                    <thead>
                      <tr>
                        <th class="text-left" style="min-width: 250px">Chỉ tiêu</th>
                        <th class="text-right" style="min-width: 150px">{{ formatMonthDisplay(previousMonth) }}</th>
                        <th class="text-right" style="min-width: 150px">{{ formatMonthDisplay(chosen_month) }}</th>
                        <th class="text-right" style="min-width: 150px">Chênh lệch</th>
                        <th class="text-right" style="min-width: 100px">Tỷ lệ</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- TÀI SẢN -->
                      <tr class="section-header">
                        <td colspan="5"><strong>TÀI SẢN</strong></td>
                      </tr>
                      <tr v-for="row in assetRows" :key="row.key" 
                          :class="{ 
                            'total-row': row.isTotal, 
                            'sub-row': row.isSubItem,
                            'expandable-row': row.isExpandable 
                          }"
                          @click="row.isExpandable ? toggleBankDetail() : null">
                        <td :class="row.isSubItem ? 'pl-4' : ''">
                          {{ row.name }}
                          <v-icon v-if="row.isExpandable" small class="ml-1">
                            {{ showBankDetail ? 'expand_less' : 'expand_more' }}
                          </v-icon>
                        </td>
                        <td class="text-right">{{ row.previous | formatCurrency }}</td>
                        <td class="text-right">{{ row.current | formatCurrency }}</td>
                        <td :class="['text-right', getDiffClass(row.diff)]">
                          {{ formatDiff(row.diff) }}
                        </td>
                        <td :class="['text-right', getDiffClass(row.percent)]">
                          {{ row.percent }}%
                        </td>
                      </tr>

                      <!-- NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU -->
                      <tr class="section-header">
                        <td colspan="5"><strong>NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU</strong></td>
                      </tr>
                      <tr v-for="row in liabilityRows" :key="row.key" :class="{ 'total-row': row.isTotal }">
                        <td>{{ row.name }}</td>
                        <td class="text-right">{{ row.previous | formatCurrency }}</td>
                        <td class="text-right">{{ row.current | formatCurrency }}</td>
                        <td :class="['text-right', getDiffClass(row.diff)]">
                          {{ formatDiff(row.diff) }}
                        </td>
                        <td :class="['text-right', getDiffClass(row.percent)]">
                          {{ row.percent }}%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="loader" class="text-center pa-4">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import moment from "moment";
import api from "../../api";
import { ExcelExporter } from "../../helpers/ExcelExporter";
import swal from "sweetalert2";

export default {
  name: "BaoCaoTinhHinhTaiChinhSoSanh",
  data() {
    return {
      loader: false,
      exporting: false,
      comparison: null,
      errorMessage: null,
      menu_month_picker: false,
      chosen_month: moment().subtract(1, "month").format("YYYY-MM"),
      showBankDetail: false, // Mặc định đóng
    };
  },
  watch: {
    chosen_month: function () {
      this.getData();
    },
  },
  computed: {
    customTitle() {
      return "Báo cáo tình hình tài chính so sánh";
    },
    previousMonth() {
      return moment(this.chosen_month).subtract(1, "month").format("YYYY-MM");
    },
    // Lấy danh sách ngân hàng unique từ cả 2 tháng
    bankDetailRows() {
      if (!this.comparison) return [];
      const { current_data, previous_data } = this.comparison;
      const bankMap = new Map();
      
      // Lấy từ previous month
      if (previous_data.ngan_hang_detail) {
        for (const bank of previous_data.ngan_hang_detail) {
          if (!bankMap.has(bank.account_code)) {
            bankMap.set(bank.account_code, { text: bank.text, account_code: bank.account_code });
          }
        }
      }
      
      // Lấy từ current month
      if (current_data.ngan_hang_detail) {
        for (const bank of current_data.ngan_hang_detail) {
          if (!bankMap.has(bank.account_code)) {
            bankMap.set(bank.account_code, { text: bank.text, account_code: bank.account_code });
          }
        }
      }
      
      return Array.from(bankMap.values());
    },
    assetRows() {
      if (!this.comparison) return [];

      const { current_data, previous_data, differences } = this.comparison;

      const rows = [
        {
          key: "tien_mat",
          name: "Tiền mặt",
          previous: previous_data.tien_mat,
          current: current_data.tien_mat,
          diff: differences.tien_mat.diff,
          percent: differences.tien_mat.percent,
        },
        {
          key: "ngan_hang",
          name: "Ngân hàng",
          previous: previous_data.ngan_hang,
          current: current_data.ngan_hang,
          diff: differences.ngan_hang.diff,
          percent: differences.ngan_hang.percent,
          isExpandable: true,
        },
      ];
      
      // Thêm chi tiết ngân hàng nếu đang mở
      if (this.showBankDetail) {
        for (const bank of this.bankDetailRows) {
          const prevAmount = this.getBankAmount(previous_data, bank.account_code);
          const currAmount = this.getBankAmount(current_data, bank.account_code);
          const diff = currAmount - prevAmount;
          const percent = prevAmount !== 0 ? ((diff / prevAmount) * 100).toFixed(2) : currAmount !== 0 ? 100 : 0;
          
          rows.push({
            key: `bank_${bank.account_code}`,
            name: `- ${bank.text}`,
            previous: prevAmount,
            current: currAmount,
            diff: diff,
            percent: parseFloat(percent),
            isSubItem: true,
          });
        }
      }
      
      rows.push(
        {
          key: "phai_thu_kh",
          name: "Phải thu khách hàng",
          previous: previous_data.phai_thu_khach_hang,
          current: current_data.phai_thu_khach_hang,
          diff: differences.phai_thu_khach_hang.diff,
          percent: differences.phai_thu_khach_hang.percent,
        },
        {
          key: "cong_no_nha_tro",
          name: "Công nợ nhà trọ",
          previous: previous_data.cong_no_nha_tro,
          current: current_data.cong_no_nha_tro,
          diff: differences.cong_no_nha_tro.diff,
          percent: differences.cong_no_nha_tro.percent,
        },
        {
          key: "phai_thu_long",
          name: "Phải thu anh Long",
          previous: previous_data.phai_thu_anh_long,
          current: current_data.phai_thu_anh_long,
          diff: differences.phai_thu_anh_long.diff,
          percent: differences.phai_thu_anh_long.percent,
        },
        {
          key: "phai_thu_khac",
          name: "Phải thu khác",
          previous: previous_data.phai_thu_khac,
          current: current_data.phai_thu_khac,
          diff: differences.phai_thu_khac.diff,
          percent: differences.phai_thu_khac.percent,
        },
        {
          key: "tong_ts",
          name: "TỔNG CỘNG TÀI SẢN",
          previous: previous_data.tong_cong_tai_san,
          current: current_data.tong_cong_tai_san,
          diff: differences.tong_cong_tai_san.diff,
          percent: differences.tong_cong_tai_san.percent,
          isTotal: true,
        }
      );
      
      return rows;
    },
    liabilityRows() {
      if (!this.comparison) return [];

      const { current_data, previous_data, differences } = this.comparison;

      return [
        {
          key: "no_dai_han",
          name: "Nợ dài hạn",
          previous: previous_data.no_dai_han,
          current: current_data.no_dai_han,
          diff: differences.no_dai_han.diff,
          percent: differences.no_dai_han.percent,
        },
        {
          key: "no_phai_tra",
          name: "- Nợ phải trả",
          previous: previous_data.no_phai_tra,
          current: current_data.no_phai_tra,
          diff: differences.no_phai_tra.diff,
          percent: differences.no_phai_tra.percent,
        },
        {
          key: "vay",
          name: "- Vay",
          previous: previous_data.vay,
          current: current_data.vay,
          diff: differences.vay.diff,
          percent: differences.vay.percent,
        },
        {
          key: "lncpp",
          name: "LNCPP",
          previous: previous_data.lncpp,
          current: current_data.lncpp,
          diff: differences.lncpp.diff,
          percent: differences.lncpp.percent,
        },
        {
          key: "tong_no",
          name: "TỔNG CỘNG NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU",
          previous: previous_data.tong_cong_no_phai_tra_va_von_chu_so_huu,
          current: current_data.tong_cong_no_phai_tra_va_von_chu_so_huu,
          diff: differences.tong_cong_no_phai_tra_va_von_chu_so_huu.diff,
          percent: differences.tong_cong_no_phai_tra_va_von_chu_so_huu.percent,
          isTotal: true,
        },
      ];
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    formatMonthDisplay(month) {
      return moment(month).format("MM/YYYY");
    },
    formatDiff(value) {
      if (value === 0) return "-";
      const formatted = Math.abs(value).toLocaleString("vi-VN");
      return value > 0 ? `+${formatted}` : `(${formatted})`;
    },
    getDiffClass(value) {
      if (value > 0) return "green--text";
      if (value < 0) return "red--text";
      return "";
    },
    toggleBankDetail() {
      this.showBankDetail = !this.showBankDetail;
    },
    getBankAmount(monthData, accountCode) {
      if (!monthData || !monthData.ngan_hang_detail) return 0;
      const bank = monthData.ngan_hang_detail.find(b => b.account_code === accountCode);
      return bank ? bank.amount : 0;
    },
    async getData() {
      this.loader = true;
      this.errorMessage = null;
      this.comparison = null;

      try {
        const result = await api.getComparisonFinancialReport(`month=${this.chosen_month}`);

        if (result.status === 200) {
          this.comparison = result.data;
        } else {
          this.errorMessage = result.message || "Không thể tải báo cáo so sánh";
        }
      } catch (error) {
        console.error("Error loading comparison report:", error);
        this.errorMessage = "Không thể tải báo cáo so sánh";
      }

      this.loader = false;
    },
    calculateDiff(prev, curr) {
      const diff = curr - prev;
      const percent = prev !== 0 ? ((diff / prev) * 100).toFixed(0) : (curr !== 0 ? 100 : 0);
      return { diff, percent: parseFloat(percent) };
    },
    async exportExcel() {
        if (!this.comparison) return;
        this.exporting = true;
        try {
            const { current_data, previous_data, differences } = this.comparison;
            const prevMonthText = this.formatMonthDisplay(this.previousMonth);
            const currMonthText = this.formatMonthDisplay(this.chosen_month);

            const columns = [
                { header: 'Chỉ tiêu', key: 'name', width: 40 },
                { header: prevMonthText, key: 'previous', width: 20, align: 'right', format: 'currency' },
                { header: currMonthText, key: 'current', width: 20, align: 'right', format: 'currency' },
                { header: 'Chênh lệch', key: 'diff', width: 20, align: 'right', format: 'currency' }, // currency format showing (-100)
                { header: 'Tỷ lệ', key: 'percent', width: 15, align: 'right' }
            ];

            const data = [];

            // Helper to add row
            const addRow = (name, prev, curr, dif, pct, style = {}) => {
                data.push({
                    name,
                    previous: prev,
                    current: curr,
                    diff: dif,
                    percent: pct + '%',
                    style,
                    outlineLevel: style.indent
                });
            };

            const addObjRow = (obj, style = {}) => {
                 addRow(obj.name, obj.previous, obj.current, obj.diff, obj.percent, style);
            };

            // --- ASSETS ---
            data.push({ name: 'TÀI SẢN', style: { bold: true, background: 'FFE3F2FD' } });

            // 1. Tien mat
            // 1. Tien mat
            // 1. Tien mat
            addRow('Tiền mặt', previous_data.tien_mat, current_data.tien_mat, differences.tien_mat.diff, differences.tien_mat.percent);
            
            // 2. Ngan hang (Always Expand)
            addRow('Ngân hàng', previous_data.ngan_hang, current_data.ngan_hang, differences.ngan_hang.diff, differences.ngan_hang.percent);
            this.bankDetailRows.forEach(bank => {
                  const prevAmount = this.getBankAmount(previous_data, bank.account_code);
                  const currAmount = this.getBankAmount(current_data, bank.account_code);
                  const diff = this.calculateDiff(prevAmount, currAmount);
                  addRow(bank.text, prevAmount, currAmount, diff.diff, diff.percent, { indent: 1 });
            });

            // 3. Phai thu KH
            addRow('Phải thu khách hàng', previous_data.phai_thu_khach_hang, current_data.phai_thu_khach_hang, differences.phai_thu_khach_hang.diff, differences.phai_thu_khach_hang.percent);

            // 4-7 Other Assets
            addRow('Công nợ nhà trọ', previous_data.cong_no_nha_tro, current_data.cong_no_nha_tro, differences.cong_no_nha_tro.diff, differences.cong_no_nha_tro.percent);
            addRow('Phải thu anh Long', previous_data.phai_thu_anh_long, current_data.phai_thu_anh_long, differences.phai_thu_anh_long.diff, differences.phai_thu_anh_long.percent);
            addRow('Phải thu khác', previous_data.phai_thu_khac, current_data.phai_thu_khac, differences.phai_thu_khac.diff, differences.phai_thu_khac.percent);

            // Total Assets
            addRow('TỔNG CỘNG TÀI SẢN', previous_data.tong_cong_tai_san, current_data.tong_cong_tai_san, differences.tong_cong_tai_san.diff, differences.tong_cong_tai_san.percent, { bold: true, background: 'FFF3E0' });


            // --- LIABILITIES ---
            data.push({ name: 'NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU', style: { bold: true, background: 'FFE3F2FD' } });

            // Iterate static liability rows logic
            // Helper to get from pre-calculated liabilityRows if possible, OR just re-map
            // Since liabilityRows are Computed, I can access `this.liabilityRows`
            this.liabilityRows.forEach(row => {
                let style = {};
                if (row.name.startsWith('-')) style.indent = 1;
                if (row.isTotal) style = { bold: true, background: 'FFF3E0' };
                
                addRow(row.name, row.previous, row.current, row.diff, row.percent, style);
            });

            await ExcelExporter.exportReport({
                title: 'BÁO CÁO TÌNH HÌNH TÀI CHÍNH SO SÁNH',
                subtitle: `${prevMonthText} - ${currMonthText}`,
                columns: columns,
                data: data,
                fileName: `BaoCaoSoSanhTaiChinh_${this.chosen_month}`
            });

        } catch (error) {
            console.error(error);
            swal.fire("Lỗi", "Không thể xuất file Excel", "error");
        }
        this.exporting = false;
    },
  },
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
}

.comparison-table th,
.comparison-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.comparison-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.comparison-table .section-header {
  background-color: #e3f2fd;
}

.comparison-table .total-row {
  background-color: #fff3e0;
  font-weight: bold;
}

.comparison-table .sub-row {
  background-color: #fafafa;
}

.comparison-table .expandable-row {
  cursor: pointer;
}

.comparison-table .expandable-row:hover {
  background-color: #f0f0f0;
}

.comparison-table tbody tr:hover {
  background-color: #fafafa;
}

.pl-4 {
  padding-left: 32px !important;
}

.green--text {
  color: #4caf50 !important;
}

.red--text {
  color: #f44336 !important;
}
</style>
